import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useContentaAuth from 'hooks/useContentaAuth';
import { authService } from 'services/Auth0Service';

import {
  CONTENTA_AUTH0_ACCESS_TOKEN,
  CONTENTA_B2B_ORGANIZATION_ID,
  CONTENTA_TOKEN,
} from 'constants/storages-values';
import {
  getFromStorage,
  removeFromStorage,
  saveInStorage,
} from 'utils/storageUtils';
import {
  AUTH_STORAGE_OPTIONS,
  getCheckSessionOptions,
  mergeTokenClaimsIntoUser,
  migrateStoredApiToken,
  persistAuthResult,
} from 'utils/authTokenUtils';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState({});
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const authUserRef = useRef(authUser);
  useEffect(() => {
    authUserRef.current = authUser;
  }, [authUser]);

  const { token } = useContentaAuth({
    authUser,
    setIsAuthenticated,
    setAuthUser,
  });

  const authenticateUser = useCallback((tokenParam = null) => {
    const apiToken = migrateStoredApiToken() || tokenParam;
    // CONTENTA_TOKEN is the signed API token. Auth0 /userinfo needs the
    // separate opaque access token saved by the callback.
    const accessToken =
      getFromStorage(CONTENTA_AUTH0_ACCESS_TOKEN) || tokenParam || apiToken;

    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    const finish = (user, signedToken = apiToken) => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsAuthenticated(true);
      setAuthUser(mergeTokenClaimsIntoUser(user, signedToken));
      setIsLoading(false);
    };

    const recoverWithAuth0Session = () => {
      authService.checkSession(
        getCheckSessionOptions(),
        (sessionError, result) => {
          if (sessionError) {
            setIsLoading(false);
            if (
              sessionError.statusCode === 401 ||
              sessionError.statusCode === 403
            ) {
              removeFromStorage(CONTENTA_TOKEN);
              removeFromStorage(CONTENTA_AUTH0_ACCESS_TOKEN);
              removeFromStorage(CONTENTA_B2B_ORGANIZATION_ID);
            }
            return;
          }

          const { accessToken: renewedAccessToken, apiToken: renewedApiToken } =
            persistAuthResult(result);

          if (!renewedAccessToken) {
            setIsLoading(false);
            return;
          }

          authService.client.userInfo(
            renewedAccessToken,
            (renewError, user) => {
              if (renewError) {
                setIsLoading(false);
                return;
              }

              finish(user, renewedApiToken || apiToken);
            }
          );
        }
      );
    };

    authService.client.userInfo(accessToken, (err, user) => {
      if (!err) {
        finish(user);
        return;
      }

      if (err.statusCode === 401 || err.statusCode === 403) {
        recoverWithAuth0Session();
        return;
      }

      setIsLoading(false);
    });
  }, []);

  const updateLocalToken = useCallback(
    (data) => {
      if (!data) return;
      removeFromStorage(CONTENTA_TOKEN);
      // Explicitly set cookie as None with Secure so it persists after cross-site redirects
      saveInStorage(CONTENTA_TOKEN, data, AUTH_STORAGE_OPTIONS);
      // Skip userInfo when user is already authenticated (token renewal flow)
      if (Object.keys(authUserRef.current).length === 0) {
        authenticateUser(data);
      }
    },
    [authenticateUser]
  );

  useEffect(() => {
    if (token) {
      updateLocalToken(token);
    }
  }, [token, updateLocalToken]);

  useEffect(() => {
    authenticateUser();
  }, [authenticateUser]);

  const value = useMemo(
    () => ({
      authUser,
      isAuthenticated,
      isLoading,
      token,
      authenticateUser,
      updateLocalToken,
      setAuthUser,
      setIsAuthenticated,
      setIsLoading,
    }),
    [
      authUser,
      isAuthenticated,
      isLoading,
      token,
      authenticateUser,
      updateLocalToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
